import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectIsHitl7Enabled } from '../../redux/features/config/config.selector';
import { selectUserProfile } from '../../selectors/user';
import {
  DEFAULT_DISPLAY_PREFERENCE,
  DisplayPreference,
} from '../../utils/displayPreference';
import {
  getDisplayPreference,
  removeDisplayPreference,
  resolvePaneUserKey,
  saveDisplayPreference,
} from '../../utils/local-storage';

/**
 * HITL-1695 — the agent's display choices, for the whole app.
 *
 * A separate context from `LayoutPreferenceContext` on purpose. That one keeps
 * live pane sizes in refs so a drag never re-renders the app; these values
 * change only on Apply, and every consumer SHOULD re-render when they do.
 *
 * `pref` is always the EFFECTIVE value: with 7.0 off it is the defaults, even
 * when a choice is stored, so a restaurant without the flag renders exactly as
 * it did (the choice only styles 7.0 surfaces, and survives for when it is on).
 */
interface DisplayPreferenceContextValue {
  pref: DisplayPreference;
  save: (pref: DisplayPreference) => void;
  reset: () => void;
}

// The default value IS the defaults, so a component rendered without the
// provider (every existing unit test) behaves exactly as it did before.
const DisplayPreferenceContext = createContext<DisplayPreferenceContextValue>({
  pref: DEFAULT_DISPLAY_PREFERENCE,
  save: () => {},
  reset: () => {},
});

export const DisplayPreferenceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userProfile = useAppSelector(selectUserProfile);
  const userKey = resolvePaneUserKey(userProfile);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);

  const [stored, setStored] = useState<DisplayPreference>(
    () => getDisplayPreference(userKey) ?? DEFAULT_DISPLAY_PREFERENCE
  );

  // A different agent signing in on this browser gets their own choices.
  useEffect(() => {
    setStored(getDisplayPreference(userKey) ?? DEFAULT_DISPLAY_PREFERENCE);
  }, [userKey]);

  const save = useCallback(
    (next: DisplayPreference) => {
      saveDisplayPreference(userKey, next);
      setStored(next);
    },
    [userKey]
  );

  const reset = useCallback(() => {
    removeDisplayPreference(userKey);
    setStored(DEFAULT_DISPLAY_PREFERENCE);
  }, [userKey]);

  const pref = isHitl7 ? stored : DEFAULT_DISPLAY_PREFERENCE;

  const value = useMemo(() => ({ pref, save, reset }), [pref, save, reset]);

  return (
    <DisplayPreferenceContext.Provider value={value}>
      {children}
    </DisplayPreferenceContext.Provider>
  );
};

/**
 * Renders `children` as if `pref` were saved — for the dialog's live preview,
 * which has to show the STAGED choice with the real components before Apply.
 */
export const DisplayPreferenceOverride: FC<{
  pref: DisplayPreference;
  children: ReactNode;
}> = ({ pref, children }) => {
  const parent = useContext(DisplayPreferenceContext);
  const value = useMemo(() => ({ ...parent, pref }), [parent, pref]);
  return (
    <DisplayPreferenceContext.Provider value={value}>
      {children}
    </DisplayPreferenceContext.Provider>
  );
};

export const useDisplayPreference = () => useContext(DisplayPreferenceContext);
